import Dialog from '@mui/material/Dialog'
import DialogContent from '@mui/material/DialogContent'
import DialogTitle from '@mui/material/DialogTitle'
import Stack from '@mui/material/Stack'
import React, { useState } from 'react'
import { FormProvider, useForm } from 'react-hook-form'
import { RegisteredTextField } from '../../../../components/fields/TextField'
import { SourceTypeSelect } from './AdminSourceTypeSelect'
import { intersection, isExternalLink } from './utils'
import { RegisteredDateTimePicker } from '../../../../components/fields/DateTimePicker'
import DialogActions from '@mui/material/DialogActions'
import Button from '@mui/material/Button'
import CircularProgress from '@mui/material/CircularProgress'
import {
    singularIssueSourceTypes,
    singularWarrantSourceTypes,
    singularConvertibleSourceTypes,
} from '../../../../utils/issues/sourceTypes'
import { SourceEntity } from 'types/SourceEntity'
import { SourceUrl } from './AdminSourceUrl'
import { SourceForm } from './AdminSourceFormRow'
import { IssueEntity } from 'types/IssueEntity'
import { WarrantEntity } from 'types/WarrantEntity'
import { ConvertibleEntity } from 'types/ConvertibleEntity'
import { SourceType } from 'types/SourceType'

export function EditSourceDialog({
    open,
    setOpen,
    source,
    sources,
    setSources,
    issue,
    warrant,
    convertible,
}: {
    open: boolean
    setOpen: (val: boolean) => void
    source: SourceEntity
    sources: SourceEntity[]
    setSources: (sources: Array<SourceEntity>) => void
    issue?: IssueEntity
    warrant?: WarrantEntity
    convertible?: ConvertibleEntity
}) {
    const formMethods = useForm<SourceForm>({ defaultValues: source })
    const [saving, setSaving] = useState(false)
    const handleOk = () => {
        if (saving) return
        setSaving(true)
        const values = formMethods.getValues()
        const newSources = [...sources]
        const index = newSources.findIndex((s) => {
            if (source.id) return s.id === source.id

            return s.sourceUrl === source.sourceUrl
        })
        newSources[index] = { ...newSources[index], ...values }
        setSources(newSources)
        setSaving(false)
        setOpen(false)
    }
    const registeredSourceTypes: SourceType[] = sources
        .map((source) => source.sourceType)
        .filter((sourceType) => sourceType !== source.sourceType)

    const issueType = issue?.type || (warrant ? 'warrant' : convertible ? 'convertible' : null)

    const singularSourceTypes = issue
        ? singularIssueSourceTypes
        : warrant
          ? singularWarrantSourceTypes
          : singularConvertibleSourceTypes

    const publicationSource = sources.find((s) => s.sourceType === 'Publication')

    const publicationDate = publicationSource?.publishedDate ? new Date(publicationSource?.publishedDate) : undefined

    return (
        <FormProvider {...formMethods}>
            <Dialog open={open} onClose={() => setOpen(false)}>
                <DialogTitle>Redigera källa</DialogTitle>
                <DialogContent>
                    <Stack spacing={2}>
                        <SourceUrl url={source.sourceUrl} />
                        <RegisteredTextField label='Intern kommentar' name='internalComment' />
                        <SourceTypeSelect
                            label='Typ'
                            name='sourceType'
                            formMethods={formMethods}
                            issueType={issueType}
                            setOfTypes={isExternalLink(source.sourceUrl) ? 'linkedSource' : 'uploadedSource'}
                            fullWidth
                            excludeSourceTypes={intersection(singularSourceTypes, registeredSourceTypes)}
                        />
                        <RegisteredTextField label='Titel' name='title' />
                        <RegisteredDateTimePicker label='Publicerad' name='publishedDate' minDate={publicationDate} />
                    </Stack>
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setOpen(false)}>Avbryt</Button>
                    <Button onClick={handleOk} disabled={saving}>
                        OK {saving && <CircularProgress size={24} />}
                    </Button>
                </DialogActions>
            </Dialog>
        </FormProvider>
    )
}
